import { useCanvasHandler } from 'hooks/useCanvasHandler';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, patchRequest, postRequest } from 'services/api';
import Swal from 'sweetalert2';
import { ACTION_TYPES, DISCOUNT_TYPES, RECURRING_TYPES, URLS } from 'utils/constants';
import { convertToHi, formatDateToString } from 'utils/dateHelpers';

// Membuat context untuk loading
const InvoiceCreateContext = createContext();

// Provider untuk context loading
export const InvoiceCreateProvider = ({ children }) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [invoice, setInvoice] = useState({
        form: {
            name: "",
            footer: "",
            enable_payment_gateway: false,
            tax: 0,
            discount: null,
            discount_type: DISCOUNT_TYPES[0].value,
            down_payment: 0,
            due_date: formatDateToString(new Date()),
            is_recurring: false,
            recurring_type: RECURRING_TYPES[2].value,
            reminder: 3,
            reminder_time: "10:00",
            items: [],
            recipients: [],
            customer: null,
        },
        data: null,
        loading: false,
        error: null,
        isSubmitting: false,
        mode: id ? ACTION_TYPES.EDIT : ACTION_TYPES.CREATE,
    });

    const formatDataForAPI = (formData) => {
        return {
            ...formData,
            ...(formData.is_recurring && { is_recurring: 1 }),
            // is_recurring: formData.is_recurring ? 1 : 0,
            ...(formData.recurring_type && { recurring_type: formData.recurring_type.value }),
            // recurring_type: formData.recurring_type ? formData.recurring_type : null,
            customer_id: formData.customer?.id,
            recipients: formData.recipients ? formData.recipients.map(recipient => recipient.value) : [],

            // Kirim items sebagai array objek
            items: formData.items.map(item => ({
                name: item.name,
                description: item.description || "-",
                qty: item.qty || 1,
                price: item.price || 0
            }))
        };
    };

    const createInvoice = useCallback(async () => {
        setInvoice((prevState) => ({
            ...prevState,
            loading: true,
            isSubmitting: true,
        }));

        const apiData = formatDataForAPI(invoice.form);

        try {
            const request = await postRequest('/invoice', apiData);
            if (request.success) {
                setInvoice((prevState) => ({
                    ...prevState,
                    data: request.data,
                    loading: false,
                    error: null,
                    isSubmitting: false,
                }));

                Swal.fire({
                    title: 'Success!',
                    text: "Invoice created successfully",
                    icon: 'success',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, do it!'
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate(URLS.v1.INVOICES);
                    }
                });
            }
        } catch (error) {
            setInvoice((prevState) => ({
                ...prevState,
                loading: false,
                error: 'Failed to create invoice',
                isSubmitting: false,
            }));

            Swal.fire({
                icon: "error",
                title: "Error!",
                text: 'Failed to create invoice',
            });
        }
    }, [invoice.form, navigate]);

    const formatDataFromAPI = (data) => {
        return {
            name: data.name,
            footer: data.footer,
            enable_payment_gateway: data.enable_payment_gateway,
            tax: data.tax,
            discount: data.discount,
            discount_type: data.discount_type,
            due_date: data.due_date,
            is_recurring: data.is_recurring,
            reminder: data.reminder,
            recurring_type: data.recurring_type,
            reminder_time: data.reminder_time && convertToHi(data.reminder_time),
            customer: data.customer,
            items: data.items,
            recipients: data.recipients.map(recipient => ({ value: recipient.id, label: recipient.name, originalData: recipient })),
        };
    };

    const fetchInvoice = useCallback(async (id) => {
        setInvoice((prev) => ({ ...prev, loading: true }));
        try {
            const response = await getRequest(`/invoice/${id}`);

            if (response.success) {
                const reValidateData = formatDataFromAPI(response.data);
                setInvoice((prev) => ({ ...prev, form: reValidateData }));
            }
        } catch (error) {
            console.error('Failed to fetch Invoice', error);
            setInvoice((prev) => ({ ...prev, error: 'Failed to fetch Invoice' }));
        } finally {
            setInvoice((prev) => ({ ...prev, loading: false }));
        }
    }, [id]);

    const updateInvoice = useCallback(async () => {
        setInvoice((prevState) => ({
            ...prevState,
            loading: true,
            isSubmitting: true,
        }));

        const apiData = formatDataForAPI(invoice.form);

        try {
            const request = await patchRequest(`/invoice/${id}`, apiData);
            if (request.success) {
                setInvoice((prevState) => ({
                    ...prevState,
                    data: request.data,
                    loading: false,
                    error: null,
                    isSubmitting: false,
                }));

                Swal.fire({
                    title: 'Success!',
                    text: "Invoice updated successfully",
                    icon: 'success',
                    showCancelButton: false,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, do it!'
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate(URLS.v1.INVOICES);
                    }
                });
            }
        } catch (error) {
            setInvoice((prevState) => ({
                ...prevState,
                loading: false,
                error: 'Failed to update invoice',
                isSubmitting: false,
            }));

            Swal.fire({
                icon: "error",
                title: "Error!",
                text: 'Failed to update invoice',
            });
        }
    }, [invoice.form, navigate, id]);

    useEffect(() => {
        if (id) {
            fetchInvoice(id);
        }
    }, [id]);

    return (
        <InvoiceCreateContext.Provider value={{ invoice, setInvoice, createInvoice, updateInvoice }}>
            {children}
        </InvoiceCreateContext.Provider>
    );
};

// Hook untuk mengakses context loading
export const useInvoiceCreate = () => {
    const context = useContext(InvoiceCreateContext);
    if (!context) {
        throw new Error('useInvoiceCreate must be used within a InvoiceCreateProvider');
    }
    return context;
};